import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { hasFeature } from '../utils/featureFlags';
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  Users, 
  FileText, 
  TrendingUp,
  Pin,
  Edit,
  Trash2,
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Download,
  FileCheck,
  Plus,
  Trash,
  FileDown,
  Mail,
  MessageSquare,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import KanbanBoard from '../components/KanbanBoard';
import ProjectModal from '../components/modals/ProjectModal';
import { Input } from '../components/ui/input';
import { getCurrencySymbol } from '../utils/currency';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [finances, setFinances] = useState(null);
  const [client, setClient] = useState(null);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);

  const checkIfPinned = React.useCallback(() => {
    const pinnedProjects = JSON.parse(localStorage.getItem('pinnedProjects') || '[]');
    setIsPinned(pinnedProjects.includes(projectId));
  }, [projectId]);

  const fetchProjectDetails = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      // Main project data is critical
      const projectRes = await apiClient.get(`/projects/${projectId}`);
      setProject(projectRes.data);

      // Check if pinned
      checkIfPinned();

      // Fetch other data in parallel, but handle errors for each individually or as a group
      try {
        const [financeRes, incomeRes, activityRes] = await Promise.all([
          apiClient.get(`/projects/${projectId}/finances`).catch(err => { console.error('Finances load failed', err); return { data: null }; }),
          apiClient.get(`/income?project_id=${projectId}`).catch(err => { console.error('Income load failed', err); return { data: [] }; }),
          apiClient.get(`/projects/${projectId}/activity`).catch(err => { console.error('Activity load failed', err); return { data: [] }; })
        ]);

        if (financeRes.data) setFinances(financeRes.data);
        setIncomeRecords(incomeRes.data || []);
        setActivities(activityRes.data || []);

        // Fetch client details if client_id exists
        if (projectRes.data.client_id) {
          apiClient.get(`/clients/${projectRes.data.client_id}`)
            .then(res => setClient(res.data))
            .catch(err => console.error('Client load failed', err));
        }
      } catch (secondaryError) {
        console.error('Secondary project data load failed', secondaryError);
      }
    } catch (error) {
      console.error('Core project details load failed', error);
      if (!silent) {
        toast.error('Failed to load project details');
        navigate('/projects');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId, checkIfPinned, navigate]);

  useEffect(() => {
    fetchProjectDetails();
    checkIfPinned();
  }, [fetchProjectDetails, checkIfPinned]);

  useEffect(() => {
    if (project && !loading) {
      const hasSeenTour = localStorage.getItem('hasSeenProjectDetailsTour');
      if (!hasSeenTour) {
        const driverObj = driver({
          showProgress: true,
          steps: [
            {
              element: '[data-testid="project-header"]',
              popover: {
                title: 'Project Overview',
                description: 'View and manage your project details, status, and important actions here.',
                side: "bottom",
                align: 'start'
              }
            },
            {
              element: '[data-testid="project-metrics"]',
              popover: {
                title: 'Key Metrics',
                description: 'Track your project budget, received payments, and overall progress at a glance.',
                side: "bottom",
                align: 'start'
              }
            },
            {
              element: '[data-testid="project-tabs"]',
              popover: {
                title: 'Project Tools',
                description: 'Switch between Kanban board, Documents, Finances, and Activity Timeline.',
                side: "top",
                align: 'start'
              }
            }
          ],
          onDestroyed: () => {
            localStorage.setItem('hasSeenProjectDetailsTour', 'true');
          }
        });

        setTimeout(() => driverObj.drive(), 1000);
      }
    }
  }, [project, loading]);

  const [newFeature, setNewFeature] = useState('');
  const [teamList, setTeamList] = useState({ items: [], total: 0, page: 1, per_page: 10 });
  const [teamQuery, setTeamQuery] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);

  const fetchProjectTeam = React.useCallback(async (q = '', page = 1) => {
    try {
      setTeamLoading(true);
      const res = await apiClient.get(`/projects/${projectId}/team`, { params: { q, page, per_page: 10 } });
      if (res.data && res.data.items) setTeamList(res.data);
      else setTeamList({ items: res.data || [], total: (res.data || []).length, page: 1, per_page: (res.data || []).length || 10 });
    } catch (err) {
      console.error('Failed to load project team', err);
    } finally {
      setTeamLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    // load first page of project team when project is available
    if (project) fetchProjectTeam('', 1);
  }, [project, fetchProjectTeam]);

  const togglePin = () => {
    if (!hasFeature(user, 'Pinning')) {
      toast.error('Pinning projects is a premium feature');
      return;
    }
    const pinnedProjects = JSON.parse(localStorage.getItem('pinnedProjects') || '[]');
    
    if (isPinned) {
      const updated = pinnedProjects.filter(id => id !== projectId);
      localStorage.setItem('pinnedProjects', JSON.stringify(updated));
      setIsPinned(false);
      toast.success('Project unpinned');
    } else {
      pinnedProjects.push(projectId);
      localStorage.setItem('pinnedProjects', JSON.stringify(pinnedProjects));
      setIsPinned(true);
      toast.success('Project pinned to dashboard');
    }
  };

  const handleMarkAsReceived = async (incomeId) => {
    try {
      await apiClient.put(`/income/${incomeId}/status`, { status: 'received' });
      toast.success('Payment marked as received');
      fetchProjectDetails(true);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleDownloadInvoice = async (incomeId) => {
    try {
      const response = await apiClient.get(`/income/${incomeId}/invoice`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${incomeId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDownloadContract = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/contract`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${project.title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download contract');
    }
  };

  const handleDownloadPRD = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/prd`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PRD_${project.title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PRD');
    }
  };

  const handleDownloadClosure = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/closure`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Closure_${project.title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download closure document');
    }
  };

  const handleSendInvoiceEmail = async (incomeId) => {
    try {
      setSendingEmail(incomeId);
      await apiClient.post(`/income/${incomeId}/send-email`);
      toast.success('Invoice email sent to client');
    } catch (error) {
      console.error('Failed to send invoice email', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice email');
    } finally {
      setSendingEmail(null);
    }
  };

  const copyToClipboard = (text, title) => {
    navigator.clipboard.writeText(text);
    toast.success(`${title} copied to clipboard`);
  };

  const handleAddFeature = async () => {
    if (!newFeature.trim()) return;
    const updatedFeatures = [...(project.features || []), newFeature.trim()];
    try {
      await apiClient.put(`/projects/${projectId}/features`, { features: updatedFeatures });
      setProject({ ...project, features: updatedFeatures });
      setNewFeature('');
      toast.success('Feature added');
    } catch (error) {
      toast.error('Failed to add feature');
    }
  };

  const handleRemoveFeature = async (index) => {
    if (!project.features) return;
    const updatedFeatures = project.features.filter((_, i) => i !== index);
    try {
      await apiClient.put(`/projects/${projectId}/features`, { features: updatedFeatures });
      setProject({ ...project, features: updatedFeatures });
      toast.success('Feature removed');
    } catch (error) {
      toast.error('Failed to remove feature');
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/projects/${projectId}`);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'acquired': 'bg-slate-100 text-slate-800 border-slate-300',
      'ongoing': 'bg-blue-100 text-blue-800 border-blue-300',
      'completed': 'bg-emerald-100 text-emerald-800 border-emerald-300'
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'acquired': <Clock className="h-4 w-4" />,
      'ongoing': <TrendingUp className="h-4 w-4" />,
      'completed': <CheckCircle2 className="h-4 w-4" />
    };
    return icons[status] || <AlertCircle className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between" data-testid="project-header">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              className="mt-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold text-slate-900">{project.title}</h1>
                <Badge className={`${getStatusColor(project.status)} border flex items-center gap-1`}>
                  {getStatusIcon(project.status)}
                  {project.status}
                </Badge>
              </div>
              <p className="text-slate-600 max-w-3xl">{project.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {hasFeature(user, 'Pinning') && (
              <Button
                variant={isPinned ? "default" : "outline"}
                size="sm"
                onClick={togglePin}
                className={isPinned ? "bg-amber-500 hover:bg-amber-600" : ""}
              >
                <Pin className={`h-4 w-4 mr-2 ${isPinned ? 'fill-current' : ''}`} />
                {isPinned ? 'Pinned' : 'Pin'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditModalOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="project-metrics">
          <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Budget</CardTitle>
              <DollarSign className="h-5 w-5" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{getCurrencySymbol(user?.currency)}{project?.total_budget?.toFixed(2) || '0.00'}</div>
              <p className="text-xs mt-1 text-indigo-100">Project value</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Received</CardTitle>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">
                {getCurrencySymbol(user?.currency)}{finances?.total_received?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {finances?.total_received && project.total_budget && project.total_budget > 0
                  ? `${((finances.total_received / project.total_budget) * 100).toFixed(0)}% of budget`
                  : 'No payments yet'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Net Profit</CardTitle>
              <TrendingUp className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${finances?.net_profit >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                {getCurrencySymbol(user?.currency)}{finances?.net_profit?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Income minus Expenses</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Progress</CardTitle>
              <TrendingUp className="h-5 w-5 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{project.progress_percentage}%</div>
              <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all"
                  style={{ width: `${project.progress_percentage}%` }}
                ></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Intelligence Insights */}
        {project && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-100 shadow-sm">
                  <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2 font-bold">
                          <TrendingUp className="h-5 w-5 text-indigo-600" />
                          Project Realization
                      </CardTitle>
                  </CardHeader>
                  <CardContent>
                      <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                  <Pie
                                      data={[
                                          { name: 'Done', value: project.progress_percentage || 0 },
                                          { name: 'To Do', value: 100 - (project.progress_percentage || 0) }
                                      ]}
                                      innerRadius={60}
                                      outerRadius={80}
                                      paddingAngle={5}
                                      dataKey="value"
                                  >
                                      <Cell fill="#10b981" />
                                      <Cell fill="#f1f5f9" />
                                  </Pie>
                                  <Tooltip />
                                  <Legend />
                              </PieChart>
                          </ResponsiveContainer>
                      </div>
                  </CardContent>
              </Card>

              <Card className="border-slate-100 shadow-sm">
                  <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2 font-bold">
                          <DollarSign className="h-5 w-5 text-emerald-600" />
                          Budget Analytics
                      </CardTitle>
                  </CardHeader>
                  <CardContent>
                      <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={[
                                  { name: 'Received', amount: finances?.total_received || 0 },
                                  { name: 'Pending', amount: finances?.total_pending || 0 }
                              ]}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <Tooltip formatter={(v) => `${getCurrencySymbol(user?.currency)}${v.toLocaleString()}`} />
                                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                                      <Cell fill="#10b981" />
                                      <Cell fill="#f59e0b" />
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </div>
                  </CardContent>
              </Card>
          </div>
        )}

        {/* Tabs Section */}
        <Tabs defaultValue={hasFeature(user, 'Kanban') ? "kanban" : "overview"} className="w-full" data-testid="project-tabs">
          <TabsList className={`grid w-full ${!hasFeature(user, 'Kanban') ? 'grid-cols-4 lg:w-[600px]' : 'grid-cols-5 lg:w-[750px]'} bg-slate-100/50 p-1`}>
            {hasFeature(user, 'Kanban') && (
              <TabsTrigger value="kanban" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Kanban Board</TabsTrigger>
            )}
            <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Documents</TabsTrigger>
            <TabsTrigger value="finances" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Finances</TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Timeline</TabsTrigger>
          </TabsList>

          {hasFeature(user, 'Kanban') && (
            <TabsContent value="kanban" className="mt-6">
              <KanbanBoard projectId={projectId} onProgressUpdate={() => fetchProjectDetails(true)} />
            </TabsContent>
          )}

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-indigo-600" />
                    Project Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Project ID</p>
                    <p className="font-medium text-slate-900">{project.project_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={`${getStatusColor(project.status)} border mt-1`}>
                      {project.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Start Date</p>
                      <p className="font-medium text-slate-900">
                        {project.start_date || 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">End Date</p>
                      <p className="font-medium text-slate-900">
                        {project.end_date || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="font-medium text-slate-900">
                      {new Date(project.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {client && (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-indigo-600" />
                      Client Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium text-slate-900">{client.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{client.email}</p>
                    </div>
                    {client.company && (
                      <div>
                        <p className="text-sm text-slate-500">Company</p>
                        <p className="font-medium text-slate-900">{client.company}</p>
                      </div>
                    )}
                    {client.phone && (
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="font-medium text-slate-900">{client.phone}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Team Members Card */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    Team Members
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-3">
                    <Input
                      placeholder="Search team members..."
                      value={teamQuery}
                      onChange={(e) => { setTeamQuery(e.target.value); fetchProjectTeam(e.target.value, 1); }}
                      className="bg-white"
                    />
                  </div>

                  {teamLoading ? (
                    <div className="flex items-center justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
                  ) : (
                    <div className="space-y-3">
                      {teamList.items && teamList.items.length > 0 ? (
                        teamList.items.map(m => (
                          <div key={m.member_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
                            <div>
                              <div className="font-medium text-slate-900">{m.name}</div>
                              <div className="text-xs text-slate-400">{m.role} • {m.email}</div>
                            </div>
                            <div>
                              <Button variant="link" size="sm" onClick={() => window.location.href = `/team/${m.member_id}`}>View</Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-500">No team members assigned to this project.</div>
                      )}

                      {/* Pagination */}
                      {teamList.total > teamList.per_page && (
                        <div className="flex items-center justify-between pt-4">
                          <div className="text-sm text-slate-500">Showing {(teamList.page - 1) * teamList.per_page + 1} - {Math.min(teamList.page * teamList.per_page, teamList.total)} of {teamList.total}</div>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => fetchProjectTeam(teamQuery, Math.max(1, teamList.page - 1))} disabled={teamList.page === 1}>Prev</Button>
                            <Button variant="outline" size="sm" onClick={() => fetchProjectTeam(teamQuery, Math.min(Math.ceil(teamList.total / teamList.per_page), teamList.page + 1))} disabled={teamList.page >= Math.ceil(teamList.total / teamList.per_page)}>Next</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-6">
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <div className="h-2 bg-indigo-600" />
                  <CardHeader>
                    <CardTitle className="text-lg">Project Documents</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button 
                      onClick={handleDownloadContract}
                      className="w-full justify-start h-12 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-100"
                      variant="outline"
                    >
                      <FileDown className="h-5 w-5 mr-3" />
                      Download Contract
                    </Button>
                    <Button 
                      onClick={handleDownloadPRD}
                      className="w-full justify-start h-12 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-100"
                      variant="outline"
                      data-testid="download-prd-btn"
                    >
                      <FileText className="h-5 w-5 mr-3" />
                      Download PRD
                    </Button>
                    <Button
                      onClick={handleDownloadClosure}
                      className="w-full justify-start h-12 text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-100"
                      variant="outline"
                      disabled={project.status !== 'completed'}
                    >
                      <CheckCircle2 className="h-5 w-5 mr-3" />
                      Download Closure Document
                    </Button>
                    <p className="text-xs text-slate-400 text-center">
                      Documents are auto-generated based on project details and features.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-2">
                <Card className="border-slate-200 shadow-sm border-2 border-dashed border-slate-200 bg-slate-50/30">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-indigo-600" />
                        Project Features
                      </CardTitle>
                      <Badge variant="outline" className="bg-white">
                        {project.features?.length || 0} Features
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          placeholder="Describe a project feature (e.g. User Authentication)"
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                          className="pr-10 bg-white"
                        />
                      </div>
                      <Button onClick={handleAddFeature} className="bg-indigo-600 hover:bg-indigo-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Add
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {project.features && project.features.length > 0 ? (
                        project.features.map((feature, index) => (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-100 group hover:border-indigo-200 hover:shadow-sm transition-all"
                          >
                            <span className="text-slate-700 flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-slate-100 text-[10px] font-bold flex items-center justify-center text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors">
                                {index + 1}
                              </span>
                              {feature}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFeature(index)}
                              className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12 bg-white rounded-xl border border-slate-100 border-dashed">
                          <p className="text-slate-400">Add project features to generate a high-quality PRD.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="finances" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card className="border-slate-100 bg-rose-50/20">
                    <CardContent className="pt-4">
                        <p className="text-xs font-bold text-rose-600 uppercase">Project Expenses</p>
                        <p className="text-2xl font-bold">{getCurrencySymbol(user?.currency)}{finances?.total_expenses?.toLocaleString()}</p>
                    </CardContent>
                </Card>
                <Card className="border-slate-100 bg-amber-50/20">
                    <CardContent className="pt-4">
                        <p className="text-xs font-bold text-amber-600 uppercase">Pending Income</p>
                        <p className="text-2xl font-bold">{getCurrencySymbol(user?.currency)}{finances?.total_pending?.toLocaleString()}</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-indigo-600" />
                  Income Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incomeRecords.length > 0 ? (
                  <div className="space-y-4">
                    {incomeRecords.map((record) => (
                      <div
                        key={record.income_id}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="font-bold text-lg text-slate-900">{getCurrencySymbol(user?.currency)}{record.amount.toLocaleString('en-IN')}</p>
                            <Badge
                              className={
                                record.payment_status === 'received'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  : record.payment_status === 'overdue'
                                  ? 'bg-rose-100 text-rose-800 border-rose-200'
                                  : 'bg-amber-100 text-amber-800 border-amber-200'
                              }
                            >
                              {record.payment_status.toUpperCase()}
                            </Badge>
                          </div>
                          {record.notes && (
                            <p className="text-sm text-slate-500 mt-1">{record.notes}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {record.payment_status === 'received'
                                ? `Received: ${record.received_date ? new Date(record.received_date).toLocaleDateString() : 'N/A'}`
                                : `Expected: ${record.expected_date ? new Date(record.expected_date).toLocaleDateString() : 'N/A'}`}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {record.payment_status !== 'received' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAsReceived(record.income_id)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-9"
                            >
                              <FileCheck className="h-4 w-4 mr-2" />
                              Mark Received
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoice(record.income_id)}
                            className="text-slate-600 h-9"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Invoice
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendInvoiceEmail(record.income_id)}
                            disabled={sendingEmail === record.income_id}
                            className="text-indigo-600 h-9 hover:bg-indigo-50"
                          >
                            <Mail className={`h-4 w-4 mr-2 ${sendingEmail === record.income_id ? 'animate-spin' : ''}`} />
                            {sendingEmail === record.income_id ? 'Sending...' : 'Send via Email'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="bg-slate-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <DollarSign className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500">No income records yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Messaging Templates Section */}
            <Card className="border-slate-200 shadow-sm mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-indigo-600" />
                  Messaging Templates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      title: "Invoice Notification",
                      description: "Send when a new invoice is issued.",
                      content: `Hi ${client?.name || 'Client'}, I hope you're doing well. I've just sent over the invoice for ${project?.title}. You can view and pay it here: [Link for payment]. Let me know if you have any questions!`,
                      icon: <Mail className="h-4 w-4 text-indigo-600" />
                    },
                    {
                      title: "Payment Reminder",
                      description: "Send for overdue or upcoming payments.",
                      content: `Hi ${client?.name || 'Client'}, just a friendly reminder that the payment for ${project?.title} is due soon. Please let me know if you need any assistance with the payment process. Thanks!`,
                      icon: <Clock className="h-4 w-4 text-amber-600" />
                    },
                    {
                      title: "Payment Received",
                      description: "Send after confirming the payment.",
                      content: `Hi ${client?.name || 'Client'}, I've received the payment for ${project?.title}. Thank you for the prompt payment! I'll continue with the next steps as planned.`,
                      icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    }
                  ].map((tmpl, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-slate-100 bg-white hover:border-indigo-200 transition-all group">
                      <div className="flex items-center gap-2 mb-2">
                        {tmpl.icon}
                        <h4 className="font-bold text-slate-900">{tmpl.title}</h4>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">{tmpl.description}</p>
                      <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 line-clamp-3 mb-4 min-h-[4.5rem]">
                        {tmpl.content}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-indigo-600 border-indigo-100 hover:bg-indigo-50"
                        onClick={() => copyToClipboard(tmpl.content, tmpl.title)}
                      >
                        <Copy className="h-3 w-3 mr-2" />
                        Copy Template
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  Project Activity Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-0">
                  {activities.length > 0 ? (
                    activities.map((act, index) => (
                      <div key={act.activity_id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-indigo-600 ring-4 ring-indigo-100' : 'bg-slate-300'}`}></div>
                          {index !== activities.length - 1 && <div className="w-0.5 h-full bg-slate-100"></div>}
                        </div>
                        <div className="pb-8">
                          <p className="font-bold text-slate-900 capitalize">{act.action} {act.entity_type}</p>
                          <p className="text-sm text-slate-600 mt-1">{act.details}</p>
                          <p className="text-[10px] text-slate-400 mt-2 font-medium flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(act.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                          <div className="w-0.5 h-full bg-slate-200"></div>
                        </div>
                        <div className="pb-8">
                          <p className="font-medium text-slate-900">Project Created</p>
                          <p className="text-sm text-slate-500">
                            {new Date(project.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <ProjectModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        project={project}
        onSuccess={fetchProjectDetails}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {project?.title}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProjectDetails;
